import React from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {MediaQuery, RoundButton} from '@optimaxdev/design-system';
import {connect} from 'react-redux';
import {useMediaQuery} from 'react-responsive';

import {useFeature} from 'components/feature';
import {CHECKOUT_DELIVERY_STEP} from 'constants/analytics/events/checkout';
import {ApplicationStoreType} from 'desktop/store';
import {AddressType} from 'desktop/types/addressType';
import {useGetCustomerData} from 'hooks/getCustomerData/getCustomerData';
import {useGetRegions} from 'hooks/location/location';
import {ShippingMethodsListType} from 'types/shippingMethodsType';

import {STEPS} from '../../constants/checkout';
import {startSetCheckoutStepSaga} from '../../store/reducers/sagaStep/sagaStep';
import {changeStepStatus} from '../../store/reducers/status/status';
import {getBillingAddress} from '../../store/selectors/billingAddress/billingAddress';
import {getIsCompleteOrderPlacing} from '../../store/selectors/completeOrder/completeOrder';
import {getAddressesSameness} from '../../store/selectors/deliveryStep/deliveryStep';
import {getSavePaymentDataIsLoading} from '../../store/selectors/paymentStep/paymentStep';
import {getShippingAddress} from '../../store/selectors/shippingAddress/shippingAddress';
import {getDeliveryStepShippingMethods} from '../../store/selectors/shippingMethods/shippingMethods';

import {Addresses} from '../addresses/addresses';

import {DeliveryStepResumeFacelift} from './deliveryStepResumeFacelift/deliveryStepResumeFacelift';
import {getResumeStructure} from './utils';

import s from './deliveryStepResume.scss';

export type PropsType = {
    shippingMethods: ShippingMethodsListType;
    shippingAddress: AddressType;
    billingAddress: AddressType;
    addressesIsSame: boolean;
    isPaymentDataSaving: boolean;
    isOrderPlacing: boolean;
    changeStep: () => void;
    markDeliveryAndPaymentStepsAsUncompleted: () => void;
};

/**
 * Delivery step resume for checkout
 *
 * @constructor
 */
export const DeliveryStepResume: React.FC<PropsType> = ({
    changeStep,
    isOrderPlacing,
    isPaymentDataSaving,
    markDeliveryAndPaymentStepsAsUncompleted,
    ...props
}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const isSmallScreen = useMediaQuery(MediaQuery.sm);
    const backToDeliveryStep = () => {
        markDeliveryAndPaymentStepsAsUncompleted();
        changeStep();
        sendDesktopAnalyticsEvent(CHECKOUT_DELIVERY_STEP, {
            message: 'Edit',
        });
    };
    const {regions} = useGetRegions(props.shippingAddress.country.value);
    const {
        currency: {symbol: currency},
    } = useGetCustomerData();

    const structure = getResumeStructure(
        {...props, alreadyReceivedStates: regions || {}, currency},
        null,
    );

    if (!structure) return null;

    const {addresses, method} = structure;

    return (
        <>
            {isFacelift ? (
                <DeliveryStepResumeFacelift
                    backToDeliveryStep={backToDeliveryStep}
                    structure={structure}
                />
            ) : (
                <div className={s.wrapper}>
                    <div className={s.completedStepIcon} />

                    <div className={s.rightSideAddressesWrapper}>
                        <div className={s.addressesWrapper}>
                            <div className={s.addresses}>
                                {addresses.map(({components, title}) => (
                                    <React.Fragment key={title}>
                                        <h3 className={s.title}>{title}</h3>
                                        <div className={s.address}>
                                            <Addresses
                                                addresses={components}
                                                isDeliveryStepResume
                                            />
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                            <div className={s.buttons}>
                                <RoundButton
                                    colorName="loblolly"
                                    isDisabled={isPaymentDataSaving || isOrderPlacing}
                                    isFull={false}
                                    onClick={backToDeliveryStep}
                                    size={isSmallScreen ? 'small' : 'medium'}
                                    variant="ghost"
                                >
                                    Edit
                                </RoundButton>
                            </div>
                        </div>

                        <div className={s.separator} />

                        <div className={s.delivery}>
                            <span className={s.title}>Shipping method:</span>
                            <span>{method}</span>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    addressesIsSame: getAddressesSameness(store),
    billingAddress: getBillingAddress(store),
    isOrderPlacing: getIsCompleteOrderPlacing(store),
    isPaymentDataSaving: getSavePaymentDataIsLoading(store),
    shippingAddress: getShippingAddress(store),
    shippingMethods: getDeliveryStepShippingMethods(store),
});

const mapDispatchToProps = {
    changeStep: () => startSetCheckoutStepSaga(STEPS.DELIVERY_STEP),
    markDeliveryAndPaymentStepsAsUncompleted: () =>
        changeStepStatus({
            [STEPS.DELIVERY_STEP]: {
                completed: false,
            },
            [STEPS.PAYMENT_STEP]: {
                completed: false,
            },
        }),
};

export const StepResumeConnected = connect(mapStateToProps, mapDispatchToProps)(DeliveryStepResume);
